import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Divider,
    Chip,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DifferenceIcon from '@mui/icons-material/Difference';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import processNarrativeService from '../../services/ProcessNarrativeService';

// Styled components
const DiffItem = styled(Box)(({ theme, type }) => ({
    padding: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
    borderRadius: theme.spacing(0.5),
    backgroundColor: 
        type === 'added' ? alpha('#4caf50', 0.1) : 
        type === 'removed' ? alpha('#f44336', 0.1) : 
        type === 'changed' ? alpha('#ff9800', 0.1) : 
        'transparent',
    borderLeft: `3px solid ${
        type === 'added' ? '#4caf50' : 
        type === 'removed' ? '#f44336' : 
        type === 'changed' ? '#ff9800' : 
        'transparent'
    }`,
}));

const DiffContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    maxHeight: '60vh',
    overflow: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#a8a8a8',
    },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
}));

/**
 * Component for comparing different versions of a process narrative
 * Highlights additions, removals, and changes between versions
 */
const NarrativeVersionCompare = ({ 
    open, 
    onClose, 
    versionId1, 
    versionId2
}) => {
    const [compareData, setCompareData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSection, setActiveSection] = useState(null);
    
    useEffect(() => {
        const fetchCompareData = async () => {
            if (!open || !versionId1 || !versionId2) {
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                
                const data = await processNarrativeService.compareNarrativeVersions(versionId1, versionId2);
                setCompareData(data);
                
                // Set the active section to the first one with differences
                if (data && data.sections) {
                    const firstDiffSection = Object.keys(data.sections).find(
                        section => data.sections[section].differences.length > 0
                    );
                    setActiveSection(firstDiffSection || Object.keys(data.sections)[0]);
                }
            } catch (error) {
                console.error("Error fetching compare data:", error);
                setError("Failed to load comparison data. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        
        fetchCompareData();
    }, [open, versionId1, versionId2]);
    
    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const renderDiffItem = (diff, index) => {
        return (
            <DiffItem key={index} type={diff.type}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: diff.type !== 'unchanged' ? 'monospace' : 'inherit',
                    }}>
                        {diff.content}
                    </Typography>
                    
                    <Box sx={{ ml: 2, minWidth: '70px', textAlign: 'right' }}>
                        {diff.type === 'added' && (
                            <Chip
                                size="small"
                                label="Added"
                                sx={{ 
                                    backgroundColor: alpha('#4caf50', 0.1), 
                                    color: '#4caf50',
                                    borderColor: alpha('#4caf50', 0.3),
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    fontWeight: 500,
                                    fontSize: '0.7rem'
                                }}
                            />
                        )}
                        {diff.type === 'removed' && (
                            <Chip
                                size="small"
                                label="Removed"
                                sx={{ 
                                    backgroundColor: alpha('#f44336', 0.1),
                                    color: '#f44336',
                                    borderColor: alpha('#f44336', 0.3),
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    fontWeight: 500,
                                    fontSize: '0.7rem'
                                }}
                            />
                        )}
                        {diff.type === 'changed' && (
                            <Chip
                                size="small"
                                label="Changed"
                                sx={{ 
                                    backgroundColor: alpha('#ff9800', 0.1),
                                    color: '#ff9800',
                                    borderColor: alpha('#ff9800', 0.3),
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    fontWeight: 500,
                                    fontSize: '0.7rem'
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </DiffItem>
        );
    };
    
    const renderNoDifferences = () => (
        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2">
                No differences found between these versions in this section.
            </Typography>
        </Alert>
    );
    
    const countDifferences = () => {
        if (!compareData || !compareData.sections) return 0;
        
        return Object.values(compareData.sections).reduce((total, section) => {
            return total + section.differences.filter(diff => diff.type !== 'unchanged').length;
        }, 0);
    };
    
    const renderSections = () => {
        if (!compareData || !compareData.sections) return null;
        
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.keys(compareData.sections).map(sectionKey => {
                    const section = compareData.sections[sectionKey];
                    const diffCount = section.differences.filter(diff => diff.type !== 'unchanged').length;
                    
                    return (
                        <Chip
                            key={sectionKey}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" component="span">
                                        {section.name}
                                    </Typography>
                                    {diffCount > 0 && (
                                        <Typography 
                                            variant="caption" 
                                            component="span" 
                                            sx={{ 
                                                ml: 0.5, 
                                                backgroundColor: alpha('#f44336', 0.1),
                                                color: '#f44336',
                                                borderRadius: '50%',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 20,
                                                height: 20,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {diffCount}
                                        </Typography>
                                    )}
                                </Box>
                            }
                            onClick={() => setActiveSection(sectionKey)}
                            variant={activeSection === sectionKey ? "filled" : "outlined"}
                            color={activeSection === sectionKey ? "primary" : "default"}
                            sx={{ mb: 1 }}
                        />
                    );
                })}
            </Box>
        );
    };
    
    return (
        <Dialog
            open={open}
            onClose={loading ? null : onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CompareArrowsIcon sx={{ mr: 1 }} />
                    Process Narrative Version Comparison
                </Box>
                <IconButton onClick={onClose} disabled={loading} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mt: 3, mb: 3 }}>
                        {error}
                    </Alert>
                ) : compareData ? (
                    <Box>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 3 
                        }}>
                            <Paper sx={{ flex: 1, minWidth: 300, p: 2, border: '1px solid #e0e0e0' }}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Version A (Older)
                                </Typography>
                                <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                                    Version {compareData.version1?.version_number || '?'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Created: {formatDate(compareData.version1?.created_at)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    By: {compareData.version1?.created_by || 'Unknown'}
                                </Typography>
                            </Paper>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ArrowRightAltIcon sx={{ color: 'text.secondary', mx: 2 }} />
                            </Box>
                            
                            <Paper sx={{ flex: 1, minWidth: 300, p: 2, border: '1px solid #e0e0e0' }}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Version B (Newer)
                                </Typography>
                                <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                                    Version {compareData.version2?.version_number || '?'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Created: {formatDate(compareData.version2?.created_at)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    By: {compareData.version2?.created_by || 'Unknown'}
                                </Typography>
                            </Paper>
                        </Box>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            backgroundColor: alpha('#f5f5f5', 0.5),
                            p: 1.5,
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <DifferenceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {countDifferences()} differences found across {Object.keys(compareData.sections || {}).length} sections
                            </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Sections
                            </Typography>
                            {renderSections()}
                        </Box>
                        
                        {activeSection && compareData.sections[activeSection] && (
                            <Box>
                                <SectionHeader variant="h6">
                                    {compareData.sections[activeSection].name}
                                </SectionHeader>
                                
                                <DiffContainer>
                                    {compareData.sections[activeSection].differences.length > 0 ? (
                                        compareData.sections[activeSection].differences
                                            .filter(diff => diff.type !== 'context')
                                            .map((diff, index) => renderDiffItem(diff, index))
                                    ) : (
                                        renderNoDifferences()
                                    )}
                                </DiffContainer>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="textSecondary">
                            No comparison data available.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NarrativeVersionCompare; 